/**
 * Demo-session API — the prospect-facing demo routes the app calls directly.
 *
 * Distinct from `lib/demoEvents.ts`, which is fire-and-forget instrumentation:
 * these are reads/writes whose ANSWER the UI shows, so failures surface as
 * thrown errors with the server's own detail rather than being swallowed.
 */

const API_BASE = "/api";

export type RippleResult = {
  /** App-relative path to the recomputed slide, through the /slidegen proxy. */
  slide_png_url: string;
  /** What the recompose actually cost, wall clock. 0-ish means a cache hit. */
  compile_ms: number;
  /** The value the SERVER used — clamped to the authored range. */
  value: number;
  cached: boolean;
};

/**
 * Recompute the demo's one editable assumption at `value`.
 *
 * Ephemeral: the server renders a throwaway deck and never touches the stored
 * artifact version. Rejections are honest — a 409 means the deliverable has
 * changed under the what-if, a 429 means the demo is being dragged faster than
 * slidegen can render.
 */
export async function recomputeRipple(value: number): Promise<RippleResult> {
  const r = await fetch(`${API_BASE}/demo/ripple`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ value }),
  });
  if (!r.ok) {
    let detail: string | undefined;
    try {
      detail = (await r.json())?.detail;
    } catch {
      // a non-JSON body (proxy error page) — the status is all we have
    }
    throw new Error(detail || `Recompute failed (${r.status})`);
  }
  return (await r.json()) as RippleResult;
}

/** One completed step of a live build, with what it ACTUALLY cost. */
export type LiveBuildStep = {
  /** Stable machine name — "instantiate" | "compile" | "attach" | "cached". */
  name: string;
  /** Display line. Server-authored so the copy stays with the step. */
  label: string;
  /** Real elapsed milliseconds for this step. Never a guess. */
  ms: number;
};

/** One headline number from the finished deliverable, server-formatted.
 *
 *  `sentiment` is typed as a plain string on purpose: this is the WIRE shape,
 *  and the server is not a type system. The narrowing to good/bad/neutral (and
 *  the dropping of half-written stats) happens in the one guard that owns it,
 *  firstRun/payload.ts `parseFirstRunOutcome`, which the rail runs over this
 *  before rendering any of it. */
export type LiveBuildOutcomeStat = {
  label: string;
  value: string;
  delta: string;
  sentiment: string;
};

/** What the built deliverable SAYS — the numbers and the decision they imply.
 *  Optional: a demo the server hasn't taught to summarise simply omits it, and
 *  the rail's reveal falls back to the provenance headline it always had. */
export type LiveBuildOutcome = {
  stats: LiveBuildOutcomeStat[];
  decision: string;
};

export type LiveBuildResult = {
  /** App-relative path to the deliverable that was just built. */
  href: string;
  artifact_title: string;
  steps: LiveBuildStep[];
  total_ms: number;
  /** True when this exact build already existed — nothing was recomposed. */
  cached: boolean;
  /** Absent on older servers and on demos with no authored outcome. Declared,
   *  never trusted — see LiveBuildOutcomeStat. */
  outcome?: LiveBuildOutcome | null;
};

/**
 * Build the demo's live deliverable, now.
 *
 * The server instantiates a parameterized deck from the demo's authored
 * facts, compiles it, and attaches it as a real artifact version — no model
 * anywhere in that path. The returned `steps` carry the REAL timings, which
 * is why the rail renders them rather than animating invented ones.
 *
 * Takes no body: the facts are server-side by design (the browser is never
 * told what the deliverable is made of before it is made).
 */
export async function runLiveBuild(): Promise<LiveBuildResult> {
  const r = await fetch(`${API_BASE}/demo/live-build`, {
    method: "POST",
    credentials: "include",
  });
  if (!r.ok) {
    let detail: string | undefined;
    try {
      detail = (await r.json())?.detail;
    } catch {
      // a non-JSON body (proxy error page) — the status is all we have
    }
    throw new Error(detail || `Live build failed (${r.status})`);
  }
  return (await r.json()) as LiveBuildResult;
}

/**
 * Fetch a recorded conversation replay.
 *
 * The ONE exception to this module's "failures surface as thrown errors" rule,
 * and deliberately so. Every caller answers a missing replay the same way — by
 * sending the prospect's question live — so there is nothing for an error to
 * tell them. A 404 (nothing registered under this thread), a network failure,
 * a proxy error page: all resolve to `null`, and the caller's fallback is a
 * real run, which is never a worse outcome than a recorded one.
 *
 * Returns the RAW body. Validation is `askReplay/payload.ts`'s job — one
 * guard, one place — so a 200 carrying nonsense degrades exactly like a 404.
 */
export async function fetchDemoReplay(threadId: string): Promise<unknown | null> {
  try {
    const r = await fetch(`${API_BASE}/demo/replay/${encodeURIComponent(threadId)}`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}
